import type { LC, PropsWithChildren } from '@use-gpu/live';

import { seq } from '@use-gpu/core';
import { provide, useMemo, useOne } from '@use-gpu/live';
import { chainTo } from '@use-gpu/shader/wgsl';
import { ScissorContext, ScissorMode, useCombinedMatrix, useCombinedScissor, useShader, useShaderRef, useShaderRefs } from '@use-gpu/workbench';

import { useRangeContext, useNoRangeContext } from '../providers/range-provider';

import { getScissorBox } from '@use-gpu/wgsl/scissor/scissor-box.wgsl';
import { getCartesianPosition } from '@use-gpu/wgsl/transform/cartesian.wgsl';

import { mat4 } from 'gl-matrix';

const NO_MATRIX = mat4.create();

export type ScissorBoxProps = PropsWithChildren<{
  matrix?: mat4,
  range?: [number, number][],

  bias?: number,
  mode?: ScissorMode,
}>;

export const ScissorBox: LC<ScissorBoxProps> = (props: ScissorBoxProps) => {
  const {matrix, mode = 'position', bias = 0, children} = props;

  const combined = useCombinedMatrix(matrix);
  const xform = useOne(() => {
    if (mode !== 'position' || !combined) return NO_MATRIX;

    const m = mat4.create();
    mat4.invert(m, combined);
    return m;
  }, [mode, combined]);
  const xf = useShaderRef(xform);

  const range = props.range ? (useNoRangeContext(), props.range) : useRangeContext();

  const min = useOne(() => seq(4).map(i => range[i]?.[0] ?? (i < 3 ? -1 : 0)), range);
  const max = useOne(() => seq(4).map(i => range[i]?.[1] ?? (i < 3 ? 1 : 2)), range);

  const bound = useShader(getScissorBox, useShaderRefs<number | number[]>(min, max, bias));

  const transform = xform ? useShader(getCartesianPosition, [xf]) : null;
  const transformed = useMemo(() => transform ? chainTo(transform, bound) : bound, [transform, bound]);

  const scissor = useCombinedScissor(transformed, mode);

  return provide(ScissorContext, scissor, children);
};
